import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'
import {
  CYCLE_GRID_TABLE,
  CYCLE_SECTOR_COLUMN,
  cycleGridMinWidth,
  CYCLE_ROW_HEIGHT,
  CYCLE_SCROLL_WRAPPER,
  CYCLE_SECTOR_WIDTH,
  CYCLE_SKELETON_VIEWPORT,
  CYCLE_STICKY_CELL,
} from '@/screens/signals/lib/cycle-map'

/*
 * The loading shapes Signal Zone's pending pages are built from, each off the
 * loaded component's own boxes so a page and its skeleton are the same height
 * block for block.
 *
 * Its own light module (Skeleton, the card class, the cycle map's boxes and
 * `cn` only): a route's `pendingComponent` ships in the entry chunk,
 * so it may not pull in a component module (docs/route-loading.md,
 * "Pitfalls").
 *
 * Bars only, no words, so a test can never find the loaded page's text here.
 * The dashboard's own shapes are in SignalZoneSkeletons.tsx.
 */

/** A bar centred in a text line box of `line` height (the `Line` idiom of MarketBoardStates). */
function Line({ line, bar }: { line: string; bar: string }) {
  return (
    <div className={cn('flex items-center', line)}>
      <Skeleton className={bar} />
    </div>
  )
}

/**
 * The date control before it loads: the shared `DatePicker`'s text trigger, one 36px box as wide
 * as "19 Aswin 2083" with its icon. `className` sizes it; the side filter's skeleton passes
 * `w-28`.
 */
export function PickerSkeleton({ className }: { className?: string }) {
  return <Skeleton className={cn('h-9 w-36 rounded-sm', className)} />
}

/**
 * Live's status line: one 12/400 line on a 16px box, as wide as "Last run at
 * 02:48 PM, 19 Aswin 2083" — narrow enough to share a 351px phone row with
 * the side filter, as the loaded line does.
 */
export function StatusLineSkeleton() {
  return <Line line="h-4" bar="h-3 w-48" />
}

/**
 * The cycle map's grid while it loads: a real table on the loaded one's boxes
 * (`lib/cycle-map.ts`), the pinned sector column, a head row and `rows` rows
 * of `columns` 48×32 cells, in a box built like `ScrollArea`'s without
 * importing it. Dev's map is 13 sectors by 12 months.
 */
export function CycleGridSkeleton({
  rows = 13,
  columns = 12,
}: {
  rows?: number
  columns?: number
}) {
  const cells = Array.from({ length: columns }, (_, index) => index)
  return (
    <div aria-hidden="true" className={cn('relative flex', CYCLE_SCROLL_WRAPPER)}>
      <div className={CYCLE_SKELETON_VIEWPORT}>
        <table className={CYCLE_GRID_TABLE} style={{ minWidth: cycleGridMinWidth(columns) }}>
          {/* The loaded table's `sr-only` caption still costs one 4px border-spacing gap. */}
          <caption className="sr-only" />
          <colgroup>
            <col className={CYCLE_SECTOR_COLUMN} />
            {cells.map((index) => (
              <col key={index} />
            ))}
          </colgroup>
          <thead>
            {/* The loaded grid's tag row ("This month"), 16px, so the card does not grow on land. */}
            <tr>
              <th className={cn('h-4', CYCLE_STICKY_CELL)} />
              {cells.map((index) => (
                <th key={index} className="h-4 p-0" />
              ))}
            </tr>
            <tr>
              <th className={cn(CYCLE_ROW_HEIGHT, CYCLE_STICKY_CELL)} />
              {cells.map((index) => (
                <th key={index} className={cn(CYCLE_ROW_HEIGHT, 'p-0')}>
                  <Skeleton className="mx-auto h-3 w-8" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, row) => (
              <tr key={row}>
                <th className={CYCLE_STICKY_CELL}>
                  <div className={cn(CYCLE_SECTOR_WIDTH, CYCLE_ROW_HEIGHT, 'flex items-center')}>
                    <Skeleton className="h-3 w-20" />
                  </div>
                </th>
                {cells.map((index) => (
                  <td key={index} className={cn(CYCLE_ROW_HEIGHT, 'p-0')}>
                    <Skeleton className="size-full" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
